import { z } from "zod/v4";
import { LEAD_GEN_AD_LANGUAGES } from "./lead-gen-ad-languages";
import { BrokerLeadCampaignFocusSchema } from "./lead-gen-focus";

/**
 * Lead Generation campaign config (what a snapshot stores) and the
 * `venturu.campaign_blueprint` v1 contract (what a playbook stores/exports).
 * Pure schemas and transforms; no database access.
 */

export const LEAD_GEN_BLUEPRINT_SCHEMA_NAME =
  "venturu.campaign_blueprint" as const;
export const LEAD_GEN_BLUEPRINT_SCHEMA_VERSION = "1" as const;

const languageSchema = z.enum(LEAD_GEN_AD_LANGUAGES);

export const LeadGenTargetingShapeSchema = z.object({
  ageMin: z.number().int().min(18).max(65),
  ageMax: z.number().int().min(18).max(65),
  interestIds: z.array(z.string()).max(12).default([]),
  behaviorIds: z.array(z.string()).max(6).default([]),
  /** Names for readability; IDs are authoritative. */
  interestNames: z.array(z.string()).default([]),
  behaviorNames: z.array(z.string()).default([]),
  logic: z.enum(["OR", "AND"]).default("OR"),
  advantageAudience: z.boolean().default(false),
});
export type LeadGenTargetingShape = z.infer<typeof LeadGenTargetingShapeSchema>;

export const LeadGenCreativeConfigSchema = z.object({
  id: z.string().optional(),
  angle: z.string(),
  language: languageSchema.default("en"),
  status: z.string().default("ACTIVE"),
  headline: z.string().nullable().default(null),
  primaryText: z.string().nullable().default(null),
  description: z.string().nullable().default(null),
  bullets: z.array(z.string()).max(3).default([]),
  imageSceneName: z.string().nullable().default(null),
  imagePrompt: z.string().nullable().default(null),
  imageLocked: z.boolean().default(false),
});
export type LeadGenCreativeConfig = z.infer<typeof LeadGenCreativeConfigSchema>;

export const LeadGenTerritoryConfigSchema = z.object({
  country: z.string(),
  code: z.string(),
  name: z.string(),
  regionCode: z.string().nullable().default(null),
  regionName: z.string().nullable().default(null),
  label: z.string(),
});

export const LeadGenFormContentSchema = z
  .object({
    greeting_headline: z.string(),
    greeting_description: z.array(z.string()),
    thank_you_description: z.string(),
  })
  .partial();

export const LeadGenCampaignConfigSchema = z.object({
  offer: z.enum(["BROKER_CONSULTATION", "FREE_VALUATION"]).optional(),
  valuationIntakeFormId: z.string().nullable().optional(),
  displayName: z.string(),
  status: z.string(),
  focus: BrokerLeadCampaignFocusSchema,
  adLanguages: z.array(languageSchema).min(1),
  monthlyAdBudgetCents: z.number().int().nonnegative(),
  territories: z.array(LeadGenTerritoryConfigSchema),
  targeting: LeadGenTargetingShapeSchema.nullable(),
  creatives: z.array(LeadGenCreativeConfigSchema),
  formContent: LeadGenFormContentSchema.nullable(),
});
export type LeadGenCampaignConfig = z.infer<typeof LeadGenCampaignConfigSchema>;

export const LeadGenCampaignPerformanceSchema = z.object({
  capturedAt: z.string(),
  spendCents: z.number().int().nonnegative(),
  impressions: z.number().int().nonnegative(),
  clicks: z.number().int().nonnegative(),
  leads: z.number().int().nonnegative(),
  costPerLeadCents: z.number().int().nonnegative().nullable(),
  sentToBroker: z.number().int().nonnegative(),
  costPerSentToBrokerCents: z.number().int().nonnegative().nullable(),
  qualifiedRate: z.number().min(0).max(1).nullable(),
  inFocusRate: z.number().min(0).max(1).nullable(),
  creatives: z
    .array(
      z.object({
        id: z.string(),
        angle: z.string(),
        status: z.string(),
        spendCents: z.number().int().nonnegative(),
        leads: z.number().int().nonnegative(),
        costPerLeadCents: z.number().int().nonnegative().nullable(),
        ctr: z.number().nullable(),
      }),
    )
    .default([]),
});
export type LeadGenCampaignPerformance = z.infer<
  typeof LeadGenCampaignPerformanceSchema
>;

export const LeadGenBlueprintAngleSchema = z.object({
  key: z.string().optional(),
  name: z.string(),
  language: languageSchema.default("en"),
  headline: z.string().nullable().default(null),
  primaryText: z.string().nullable().default(null),
  description: z.string().nullable().default(null),
  bullets: z.array(z.string()).max(3).default([]),
  imageSceneName: z.string().nullable().default(null),
  note: z.string().nullable().default(null),
  /** Exemplar copy is inspiration for the copywriter, never reused verbatim. */
  exemplar: z.literal(true).default(true),
});

export const CampaignBlueprintV1Schema = z.object({
  offer: z.enum(["BROKER_CONSULTATION", "FREE_VALUATION"]).optional(),
  schema_name: z.literal(LEAD_GEN_BLUEPRINT_SCHEMA_NAME),
  schema_version: z.literal(LEAD_GEN_BLUEPRINT_SCHEMA_VERSION),
  name: z.string().min(2).max(120),
  description: z.string().max(600).nullable().default(null),
  tags: z.array(z.string()).default([]),
  focus: BrokerLeadCampaignFocusSchema,
  languages: z.array(languageSchema).min(1).default(["en"]),
  geography: z
    .object({
      depth: z.literal("county").default("county"),
      recommendedMinAreas: z.number().int().min(1).max(20).default(5),
      countries: z.array(z.string()).default(["US"]),
      note: z.string().nullable().default(null),
    })
    .default({
      depth: "county",
      recommendedMinAreas: 5,
      countries: ["US"],
      note: null,
    }),
  targeting_shape: LeadGenTargetingShapeSchema.nullable().default(null),
  angles: z.array(LeadGenBlueprintAngleSchema).default([]),
  image_direction: z.array(z.string()).default([]),
  form_rules: z.array(z.string()).default([]),
  defaults: z
    .object({
      monthlyAdBudgetCents: z
        .number()
        .int()
        .nonnegative()
        .nullable()
        .default(null),
      creativesPerLanguage: z.number().int().min(1).max(6).default(3),
    })
    .default({ monthlyAdBudgetCents: null, creativesPerLanguage: 3 }),
  why_this_works: z.array(z.string()).default([]),
  known_pitfalls: z.array(z.string()).default([]),
  pre_launch_gates: z.array(z.string()).default([]),
  performance_summary: LeadGenCampaignPerformanceSchema.partial()
    .nullable()
    .default(null),
  provenance: z
    .object({
      exportedAt: z.string(),
      source: z.string().default("venturu"),
      sourceCampaignId: z.string().nullable().default(null),
      sourceSnapshotVersion: z.number().int().nullable().default(null),
    })
    .optional(),
});
export type CampaignBlueprintV1 = z.infer<typeof CampaignBlueprintV1Schema>;

/** Parse untrusted JSON (import) into a blueprint or throw a readable error. */
export function parseCampaignBlueprintJson(
  input: unknown,
): CampaignBlueprintV1 {
  const raw = typeof input === "string" ? JSON.parse(input) : input;
  const result = CampaignBlueprintV1Schema.safeParse(raw);
  if (!result.success) {
    const issue = result.error.issues[0];
    throw new Error(
      `Invalid campaign blueprint${issue ? ` at ${issue.path.join(".") || "root"}: ${issue.message}` : ""}`,
    );
  }
  return result.data;
}

export * from "./lead-gen-blueprint-transforms";
